import { gql } from "@apollo/client";
import { useCallback, useRef } from "react";
import {
  UpdateNotificationPreferencesInput,
  UserRole,
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
} from "../../generated/graphql";
import { useUserRoles } from "../../modules/auth";

export const useNotificationPreferences = () => {
  const roles = useUserRoles();
  const isOrderModerator = roles.includes(UserRole.OrderModerator);

  const { data, loading, error, refetch } = useGetNotificationPreferencesQuery({
    skip: !isOrderModerator,
    context: { clientName: "bff" },
    fetchPolicy: "network-only",
  });

  const [mutateAsync, { loading: updating }] =
    useUpdateNotificationPreferencesMutation({
      context: { clientName: "bff" },
    });

  // Each toggle fires its own write immediately (no "save" button), so rapid
  // toggling can send several PUTs at once. The endpoint replaces the whole
  // preference set on every write, so if those requests resolve out of
  // order, a late-arriving one built from older local state can silently
  // overwrite a newer one. Chaining them through pendingRef guarantees they
  // reach the server in the same order the user made them.
  const pendingRef = useRef<Promise<unknown>>(Promise.resolve());

  const updatePreferences = useCallback(
    (input: UpdateNotificationPreferencesInput) => {
      // No refetch after the write: the mutation already returns the full
      // updated preference set, and the caller tracks its own optimistic
      // state, so re-running the GET query on every toggle is pure extra
      // traffic. The GET only runs once, on mount.
      const run = () => mutateAsync({ variables: { input } });

      const result = pendingRef.current.then(run, run);
      pendingRef.current = result.catch(() => {});
      return result;
    },
    [mutateAsync],
  );

  return {
    isOrderModerator,
    data: data?.NotificationPreferences.find,
    loading,
    updating,
    error,
    updatePreferences,
    refetch,
  };
};

gql`
  query GetNotificationPreferences {
    NotificationPreferences {
      find {
        requisitionStatuses {
          pendingApproval
          approved
          rejected
          closed
        }
        orderStatuses {
          draft
          inRevision
          pendingApproval
          approved
          awaitingDelivery
          delivered
        }
      }
    }
  }
`;

gql`
  mutation UpdateNotificationPreferences(
    $input: UpdateNotificationPreferencesInput!
  ) {
    NotificationPreferences {
      update(input: $input) {
        requisitionStatuses {
          pendingApproval
          approved
          rejected
          closed
        }
        orderStatuses {
          draft
          inRevision
          pendingApproval
          approved
          awaitingDelivery
          delivered
        }
      }
    }
  }
`;
